import { jwtDecode, JwtPayload } from 'jwt-decode';
import { Dispatch as ReduxDispatch } from 'redux';
import moment from 'moment';

import ItemReducerType from '../../common/store/ItemReducerType';
import { BasicAction, PayloadAction } from '../../common/store/redux';
import { GenericItemReducerAction } from '../../common/store/ReduxHelper';
import UserRole from '../../common/types/UserRole';

export interface LoginDto {
  access:  string;
  refresh: string;
}

export interface UserAccount {
  id:       number;
  username: string;
  email:    string;
  role:     UserRole;
  remember: boolean;
  loginIat: number;
  token:    string;
  refresh?: string;
}

export interface OwnrecipesPayload extends JwtPayload {
  user_id:  number | string;
  username: string;
  email:    string;
  is_staff: boolean;
}

function getRole(decodedToken: OwnrecipesPayload): UserRole {
  const username = decodedToken.username?.toLocaleLowerCase();
  if (username == null) return UserRole.GUEST;

  if (username.startsWith('guest') || username.startsWith('gast')) return UserRole.GUEST;
  else if (['admin'].includes(username)) return UserRole.ADMIN;
  else if (decodedToken.is_staff) return UserRole.STAFF;
  else return UserRole.USER;
}

export const toUserAccount = (loginDto: LoginDto, remember: boolean, loginIat: number | undefined): UserAccount => {
  const { access, refresh } = loginDto;
  if (access == null) throw new Error('Invalid response: access token may not be null');
  const decodedToken: OwnrecipesPayload | undefined = jwtDecode<OwnrecipesPayload>(access);

  if (decodedToken.user_id == null)  throw new Error('Invalid response: The token is incomplete (user_id is missing)');
  if (decodedToken.username == null) throw new Error('Invalid response: The token is incomplete (username is missing)');
  if (decodedToken.email == null)    throw new Error('Invalid response: The token is incomplete (email is missing)');

  const tokenUserId = typeof decodedToken.user_id === 'string' ? parseInt(decodedToken.user_id as string) : decodedToken.user_id;
  if (!Number.isInteger(tokenUserId) || tokenUserId < 0) throw new Error('Invalid response: The token is corrupted (user_id must be a positive integer)');

  return {
    id:       tokenUserId,
    token:    access,
    refresh:  refresh,
    username: decodedToken.username,
    email:    decodedToken.email,
    role:     getRole(decodedToken),
    remember: remember,
    loginIat: loginIat || decodedToken.iat || moment().unix(),
  };
};

export enum AccountActionTypes {
  LOGIN  = 'LOGIN',
  SIDELOAD_TOKEN = 'SIDELOAD_TOKEN',
  INVALIDATE_TOKEN = 'INVALIDATE_TOKEN',
  FORGET_LOGIN = 'FORGET_LOGIN',
  LOGOUT = 'LOGOUT',
}

export const ACCOUNT_STORE = 'account';
export const ACCOUNT_TOKEN_STORAGE_KEY = 'token';

export type IAccountLoginAction = {
  store: typeof ACCOUNT_STORE;
  typs:  typeof AccountActionTypes.LOGIN;
} & PayloadAction<UserAccount>;

export type IAccountSideloadTokenAction = {
  store: typeof ACCOUNT_STORE;
  typs:  typeof AccountActionTypes.SIDELOAD_TOKEN;
} & PayloadAction<UserAccount>;

export type IAccountInvalidateTokenAction = {
  store: typeof ACCOUNT_STORE;
  typs:  typeof AccountActionTypes.INVALIDATE_TOKEN;
} & BasicAction;

export type IAccountForgetLoginAction = {
  store: typeof ACCOUNT_STORE;
  typs:  typeof AccountActionTypes.FORGET_LOGIN;
} & BasicAction;

export type IAccountLogoutAction = {
  store: typeof ACCOUNT_STORE;
  typs:  typeof AccountActionTypes.LOGOUT;
} & BasicAction;

export type AccountState    = ItemReducerType<UserAccount> & { valid?: boolean };
export type AccountAction   = IAccountLoginAction | IAccountSideloadTokenAction | IAccountInvalidateTokenAction | IAccountForgetLoginAction | IAccountLogoutAction | GenericItemReducerAction<UserAccount>;
export type AccountDispatch = ReduxDispatch<AccountAction>;
